import { InfiniteScroll, LoadStatus, Loader } from '@servicetitan/cp-ui';
import { BodyText, Divider } from '@servicetitan/design-system';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import { observer } from 'mobx-react';
import { FC, Fragment, PropsWithChildren, useEffect } from 'react';
import { JobAppointmentsListDataStore } from '../stores/appointment-list-data-store';
import { JobAppointmentsItem } from './job-appointments-item';

interface JobAppointmentsListProps {
    jobId: number;
    appointmentCount: number;
    isDrawer: boolean;
}

export const JobAppointmentsListProvider: FC<PropsWithChildren> = provide({
    singletons: [JobAppointmentsListDataStore],
    // eslint-disable-next-line react/jsx-no-useless-fragment
})(({ children }) => <Fragment>{children}</Fragment>);

export const JobAppointmentsList: FC<JobAppointmentsListProps> = props => {
    return (
        <JobAppointmentsListProvider>
            <JobAppointmentsListUnwrapped {...props} />
        </JobAppointmentsListProvider>
    );
};

export const JobAppointmentsListUnwrapped: FC<JobAppointmentsListProps> = observer(
    ({ appointmentCount, isDrawer, jobId }) => {
        const [{ appointments, hasMore, loadJobAppointments, loadStatus, page }] = useDependencies(
            JobAppointmentsListDataStore
        );

        useEffect(() => {
            loadJobAppointments(jobId, 1);
        }, [jobId, loadJobAppointments]);

        const loadNextPage = () => {
            return loadJobAppointments(jobId, page + 1);
        };

        const initialLoading = !appointments && loadStatus === LoadStatus.Loading;
        const loadingTitle = 'Loading appointments';

        return (
            <Fragment>
                <BodyText bold>
                    {`${appointmentCount} ${appointmentCount === 1 ? 'Appointment' : 'Appointments'}`}
                </BodyText>

                <Divider spacing="2" />
                {initialLoading && <Loader text={loadingTitle} />}
                {!!appointments?.length && appointments?.length > 0 && (
                    <InfiniteScroll hasMore={hasMore} next={loadNextPage}>
                        {appointments.map(appointment => (
                            <JobAppointmentsItem
                                key={appointment.id}
                                appointment={appointment}
                                className="m-b-2"
                                isDrawer={isDrawer}
                            />
                        ))}
                    </InfiniteScroll>
                )}
            </Fragment>
        );
    }
);
